import React, { useCallback, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, IconButton, InputLabel, List, ListItem, ListItemSecondaryAction, ListItemText,
  MenuItem, Paper, Select, Stack, Tooltip, Typography,
} from '@mui/material';
import { CloudUpload, ContentCopy, Delete, Download, FindReplace, Link as LinkIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import copy from 'copy-to-clipboard';
import { EUserGroup, ProductExampleAttachmentDto } from 'apiClient/Api';
import { buildAttachmentRef } from 'Hooks/useAttachmentTokens';
import { useApplicationContext } from 'ApplicationContext';
import axios from 'axios';

interface AttachmentManagerProps {
  exampleId: string | null;
  attachments: ProductExampleAttachmentDto[];
  onChange: (next: ProductExampleAttachmentDto[]) => void;
  /** Called when an attachment id is replaced — UI can rewrite markdown refs. */
  onAttachmentReplaced?: (oldId: string, next: ProductExampleAttachmentDto) => void;
  /** Insert the markdown-ref snippet at the current editor caret. */
  onInsertRef?: (att: ProductExampleAttachmentDto) => void;
  /** Default required user group for new uploads (derived from example status). */
  defaultUserGroup: EUserGroup;
  disabled?: boolean;
}

const USER_GROUP_VALUES: EUserGroup[] = [
  EUserGroup.SUPER_ADMIN,
  EUserGroup.ADMIN,
  EUserGroup.DEVELOPMENT,
  EUserGroup.OFFICE,
  EUserGroup.CUSTOMER,
];

/**
 * Renders size as KB/MB with one decimal.
 */
const formatSize = (bytes: number | undefined): string => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Attachment panel for the edit page: upload, replace, delete, copy-ref,
 * insert-ref. Persistence is immediate against the backend; the parent
 * form is notified via `onChange` so the attachment list stays in sync
 * with the `GetProductExampleData` payload.
 */
export const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  exampleId,
  attachments,
  onChange,
  onAttachmentReplaced,
  onInsertRef,
  defaultUserGroup,
  disabled,
}) => {
  const { t } = useTranslation();
  const context = useApplicationContext();
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProductExampleAttachmentDto | null>(null);
  const [nextUserGroup, setNextUserGroup] = useState<EUserGroup>(defaultUserGroup);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetRef = useRef<ProductExampleAttachmentDto | null>(null);

  React.useEffect(() => { setNextUserGroup(defaultUserGroup); }, [defaultUserGroup]);

  const upload = useCallback(async (file: File, userGroup: EUserGroup): Promise<ProductExampleAttachmentDto | null> => {
    if (!exampleId) return null;
    const response = await context.api.productExample.productExampleUploadAttachment(exampleId, {
      File: file,
      RequiredUserGroup: EUserGroup[userGroup],
    });
    return response.data ?? null;
  }, [context.api.productExample, exampleId]);

  const handleUpload = useCallback(async (files: FileList | null) => {
    if (!exampleId || !files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: ProductExampleAttachmentDto[] = [];
      for (const file of Array.from(files)) {
        try {
          const att = await upload(file, nextUserGroup);
          if (att) uploaded.push(att);
        } catch (err: any) {
          enqueueSnackbar(err?.response?.data?.detail ?? t('productExamples.attachments.uploadError', { name: file.name }), { variant: 'error' });
        }
      }
      if (uploaded.length > 0) {
        onChange([...attachments, ...uploaded]);
        enqueueSnackbar(t('productExamples.attachments.uploaded', { count: uploaded.length }), { variant: 'success' });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [attachments, enqueueSnackbar, exampleId, nextUserGroup, onChange, t, upload]);

  const handleReplace = useCallback(async (files: FileList | null) => {
    const target = replaceTargetRef.current;
    replaceTargetRef.current = null;
    if (replaceInputRef.current) replaceInputRef.current.value = '';
    if (!target?.id || !exampleId || !files || files.length === 0) return;
    setBusyId(target.id);
    try {
      const next = await upload(files[0], (target.requiredUserGroup ?? defaultUserGroup));
      if (!next?.id) return;
      try {
        await context.api.productExample.productExampleDeleteAttachment(exampleId, target.id);
      } catch {
        // if delete fails the old attachment lingers — user can delete manually
      }
      onChange([...attachments.filter((a) => a.id !== target.id), next]);
      onAttachmentReplaced?.(target.id, next);
      enqueueSnackbar(t('productExamples.attachments.replaced'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail ?? t('productExamples.attachments.replaceError'), { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }, [attachments, context.api.productExample, defaultUserGroup, enqueueSnackbar, exampleId, onAttachmentReplaced, onChange, t, upload]);

  const handleDelete = useCallback(async (att: ProductExampleAttachmentDto) => {
    if (!att.id || !exampleId) return;
    setBusyId(att.id);
    try {
      await context.api.productExample.productExampleDeleteAttachment(exampleId, att.id);
      onChange(attachments.filter((a) => a.id !== att.id));
      enqueueSnackbar(t('productExamples.attachments.deleted'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail ?? t('productExamples.attachments.deleteError'), { variant: 'error' });
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  }, [attachments, context.api.productExample, enqueueSnackbar, exampleId, onChange, t]);

  const handleDownload = useCallback(async (att: ProductExampleAttachmentDto) => {
    if (!att.id || !exampleId) return;
    try {
      const response = await axios.get(`${context.baseUrl}/ProductExample/${exampleId}/attachments/${att.id}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${context.token}` },
        params: { download: true },
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.fileName ?? 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar(t('productExamples.attachments.downloadError'), { variant: 'error' });
    }
  }, [context.baseUrl, context.token, enqueueSnackbar, exampleId, t]);

  const handleCopyRef = useCallback((att: ProductExampleAttachmentDto) => {
    if (!att.id) return;
    const snippet = att.markdownRef ?? `![${att.fileName ?? ''}](${buildAttachmentRef(att.id)})`;
    if (copy(snippet)) {
      enqueueSnackbar(t('productExamples.attachments.copied'), { variant: 'success' });
    }
  }, [enqueueSnackbar, t]);

  if (!exampleId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="text.secondary">{t('productExamples.attachments.needsDraft')}</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => handleUpload(e.target.files)}
      />
      <input
        ref={replaceInputRef}
        type="file"
        hidden
        onChange={(e) => handleReplace(e.target.files)}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16}/> : <CloudUpload/>}
          disabled={uploading || disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('productExamples.attachments.upload')}
        </Button>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="attachment-default-group">{t('productExamples.attachments.defaultGroup')}</InputLabel>
          <Select
            labelId="attachment-default-group"
            label={t('productExamples.attachments.defaultGroup')}
            value={nextUserGroup}
            onChange={(e) => setNextUserGroup(e.target.value as EUserGroup)}
            disabled={disabled}
          >
            {USER_GROUP_VALUES.map((g) => (
              <MenuItem key={g} value={g}>{EUserGroup[g]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {attachments.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('productExamples.attachments.empty')}</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <List dense disablePadding>
            {attachments.map((att) => (
              <ListItem
                key={att.id}
                divider
                sx={{ py: 1 }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{att.fileName}</Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={att.requiredUserGroup != null ? EUserGroup[att.requiredUserGroup] : '—'}
                      />
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {[att.contentType, formatSize(att.sizeBytes)].filter(Boolean).join(' · ')}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={0.5}>
                    {onInsertRef && (
                      <Tooltip title={t('productExamples.attachments.insert')}>
                        <IconButton size="small" onClick={() => onInsertRef(att)} disabled={disabled}>
                          <LinkIcon fontSize="small"/>
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('productExamples.attachments.copyRef')}>
                      <IconButton size="small" onClick={() => handleCopyRef(att)}>
                        <ContentCopy fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('productExamples.attachments.download')}>
                      <IconButton size="small" onClick={() => handleDownload(att)}>
                        <Download fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('productExamples.attachments.replace')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          replaceTargetRef.current = att;
                          replaceInputRef.current?.click();
                        }}
                        disabled={disabled || busyId === att.id}
                      >
                        {busyId === att.id ? <CircularProgress size={16}/> : <FindReplace fontSize="small"/>}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('productExamples.attachments.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDelete(att)}
                        disabled={disabled || busyId === att.id}
                      >
                        <Delete fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('productExamples.attachments.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('productExamples.attachments.deleteMessage', { name: confirmDelete?.fileName ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('productExamples.attachments.cancel')}</Button>
          <Button color="error" onClick={() => confirmDelete && handleDelete(confirmDelete)}>
            {t('productExamples.attachments.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
